export type DemoLanguage = 'pt-BR' | 'en-GB';

export interface DemoTranslations {
  // Currency and locale settings
  locale: string;
  currency: string;
  // DemoPage
  demo: {
    startDemo: string;
    restart: string;
    reset: string;
    registered: string;
    automatic: string;
    phase: string;
    phases: {
      idle: string;
      whatsapp: string;
      transition: string;
      dashboard: string;
      complete: string;
    };
    hints: {
      start: string;
      reset: string;
      hideControls: string;
    };
  };
  // WhatsApp Demo Message
  whatsappDemo: {
    messageText: string;
    description: string;
    responseMessage: string;
    amount: number;
  };
  // Dashboard summary values (realistic for each market)
  dashboardValues: {
    balance: number;
    income: number;
    expense: number;
    count: number;
    transactions: Array<{
      type: 'income' | 'expense';
      description: string;
      category: string;
      amount: number;
      date: string;
    }>;
  };
  // Dashboard
  dashboard: {
    title: string;
    subtitle: string;
    balance: string;
    income: string;
    expenses: string;
    transactions: string;
    byCategory: string;
    recentTransactions: string;
    now: string;
    // Navigation
    nav: {
      home: string;
      transactions: string;
      pots: string;
      profile: string;
    };
  };
  // Categories
  categories: {
    food: string;
    transport: string;
    housing: string;
    salary: string;
    freelance: string;
    education: string;
    health: string;
    entertainment: string;
    travel: string;
    bills: string;
    others: string;
  };
  // WhatsApp UI
  whatsapp: {
    message: string;
    today: string;
    online: string;
  };
}

export const translations: Record<DemoLanguage, DemoTranslations> = {
  'pt-BR': {
    locale: 'pt-BR',
    currency: 'BRL',
    demo: {
      startDemo: 'Iniciar Demo',
      restart: 'Reiniciar',
      reset: 'Reset',
      registered: 'registrado!',
      automatic: 'automático',
      phase: 'Fase',
      phases: {
        idle: 'Aguardando',
        whatsapp: 'WhatsApp',
        transition: 'Transição',
        dashboard: 'Dashboard',
        complete: 'Completo',
      },
      hints: {
        start: 'Iniciar',
        reset: 'Reset',
        hideControls: 'Esconder controles',
      },
    },
    whatsappDemo: {
      messageText: '45.90 almoço cliente #food',
      description: 'Almoço cliente',
      responseMessage: '✅ Transação registrada!\n\n💰 R$ 45,90\n📁 Alimentação\n📝 Almoço cliente\n\nAcesse o dashboard para mais detalhes.',
      amount: 45.90,
    },
    dashboardValues: {
      balance: 13651.80,
      income: 18300,
      expense: 4648.20,
      count: 12,
      transactions: [
        { type: 'income', description: 'Salário janeiro', category: 'salary', amount: 12000, date: '05 Jan' },
        { type: 'income', description: 'Projeto freelance', category: 'freelance', amount: 5500, date: '08 Jan' },
        { type: 'expense', description: 'Coworking', category: 'housing', amount: 1250, date: '10 Jan' },
        { type: 'expense', description: 'Uber aeroporto', category: 'transport', amount: 89, date: '11 Jan' },
      ],
    },
    dashboard: {
      title: 'Dashboard',
      subtitle: 'Visão geral das suas finanças',
      balance: 'Saldo',
      income: 'Receitas',
      expenses: 'Despesas',
      transactions: 'Transações',
      byCategory: 'Por Categoria',
      recentTransactions: 'Transações Recentes',
      now: 'Agora',
      nav: {
        home: 'Home',
        transactions: 'Transações',
        pots: 'Pots',
        profile: 'Perfil',
      },
    },
    categories: {
      food: 'Alimentação',
      transport: 'Transporte',
      housing: 'Moradia',
      salary: 'Salário',
      freelance: 'Freelance',
      education: 'Educação',
      health: 'Saúde',
      entertainment: 'Entretenimento',
      travel: 'Viagem',
      bills: 'Contas',
      others: 'Outros',
    },
    whatsapp: {
      message: 'Mensagem',
      today: 'Hoje',
      online: 'online',
    },
  },
  'en-GB': {
    locale: 'en-GB',
    currency: 'GBP',
    demo: {
      startDemo: 'Start Demo',
      restart: 'Restart',
      reset: 'Reset',
      registered: 'registered!',
      automatic: 'automatic',
      phase: 'Phase',
      phases: {
        idle: 'Waiting',
        whatsapp: 'WhatsApp',
        transition: 'Transition',
        dashboard: 'Dashboard',
        complete: 'Complete',
      },
      hints: {
        start: 'Start',
        reset: 'Reset',
        hideControls: 'Hide controls',
      },
    },
    whatsappDemo: {
      messageText: '12.50 client lunch #food',
      description: 'Client lunch',
      responseMessage: '✅ Transaction recorded!\n\n💰 £12.50\n📁 Food\n📝 Client lunch\n\nCheck the dashboard for more details.',
      amount: 12.50,
    },
    dashboardValues: {
      balance: 892.35,
      income: 1450,
      expense: 557.65,
      count: 12,
      transactions: [
        { type: 'income', description: 'Weekly salary', category: 'salary', amount: 650, date: '05 Jan' },
        { type: 'income', description: 'Freelance project', category: 'freelance', amount: 350, date: '08 Jan' },
        { type: 'expense', description: 'Coworking space', category: 'housing', amount: 85, date: '10 Jan' },
        { type: 'expense', description: 'Uber to airport', category: 'transport', amount: 42, date: '11 Jan' },
      ],
    },
    dashboard: {
      title: 'Dashboard',
      subtitle: 'Overview of your finances',
      balance: 'Balance',
      income: 'Income',
      expenses: 'Expenses',
      transactions: 'Transactions',
      byCategory: 'By Category',
      recentTransactions: 'Recent Transactions',
      now: 'Now',
      nav: {
        home: 'Home',
        transactions: 'Transactions',
        pots: 'Pots',
        profile: 'Profile',
      },
    },
    categories: {
      food: 'Food',
      transport: 'Transport',
      housing: 'Housing',
      salary: 'Salary',
      freelance: 'Freelance',
      education: 'Education',
      health: 'Health',
      entertainment: 'Entertainment',
      travel: 'Travel',
      bills: 'Bills',
      others: 'Others',
    },
    whatsapp: {
      message: 'Message',
      today: 'Today',
      online: 'online',
    },
  },
};
